import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../theme";

interface PopularQuestionsProps {
  onSelectQuestion: (question: string) => void;
  disabled?: boolean;
  /** From GET /v1/config.welcome_message — omitted when empty. */
  greeting?: string;
  /** From GET /v1/config.popular_questions — no i18n fallback. */
  questions?: string[];
}

const styles: Record<string, CSSProperties> = {
  section: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    marginTop: tokens.space[2],
  },
  greeting: {
    margin: `0 0 ${tokens.space[3]}`,
    fontSize: tokens.font.size.xl,
    fontWeight: tokens.font.weight.bold,
    color: tokens.text.primary,
    letterSpacing: "-0.02em",
    lineHeight: tokens.font.lineHeight.tight,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.space[2],
    listStyle: "none",
    margin: 0,
    padding: 0,
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: tokens.space[3],
    width: "100%",
    textAlign: "left",
    padding: `${tokens.space[3]} 14px`,
    background: tokens.surface.chip,
    border: `1px solid ${tokens.border.default}`,
    borderRadius: tokens.radius.pill,
    cursor: "pointer",
    fontSize: tokens.font.size.md,
    fontWeight: tokens.font.weight.medium,
    color: tokens.text.secondary,
    fontFamily: "inherit",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  },
  iconWrap: {
    width: 28,
    height: 28,
    borderRadius: tokens.radius.sm,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    background: tokens.icon.sparklesBg,
  },
};

function SparklesIcon() {
  return (
    <span style={styles.iconWrap}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 50 50"
        aria-hidden="true"
      >
        <path
          fill={tokens.icon.accent}
          d="M22.462 11.035l2.88 7.097c1.204 2.968 3.558 5.322 6.526 6.526l7.097 2.88c1.312.533 1.312 2.391 0 2.923l-7.097 2.88c-2.968 1.204-5.322 3.558-6.526 6.526l-2.88 7.097c-.533 1.312-2.391 1.312-2.923 0l-2.88-7.097c-1.204-2.968-3.558-5.322-6.526-6.526l-7.097-2.88c-1.312-.533-1.312-2.391 0-2.923l7.097-2.88c2.968-1.204 5.322-3.558 6.526-6.526l2.88-7.097C20.071 9.723 21.929 9.723 22.462 11.035zM39.945 2.701l.842 2.428c.664 1.915 2.169 3.42 4.084 4.084l2.428.842c.896.311.896 1.578 0 1.889l-2.428.842c-1.915.664-3.42 2.169-4.084 4.084l-.842 2.428c-.311.896-1.578.896-1.889 0l-.842-2.428c-.664-1.915-2.169-3.42-4.084-4.084l-2.428-.842c-.896-.311-.896-1.578 0-1.889l2.428-.842c1.915-.664 3.42-2.169 4.084-4.084l.842-2.428C38.366 1.805 39.634 1.805 39.945 2.701z"
        />
      </svg>
    </span>
  );
}

export default function PopularQuestions({
  onSelectQuestion,
  disabled,
  greeting,
  questions = [],
}: PopularQuestionsProps) {
  const { t } = useTranslation();
  const greetingText = greeting?.trim() || "";
  const chips = questions.filter((item) => item.trim().length > 0);

  if (!greetingText && chips.length === 0) {
    return null;
  }

  return (
    <section style={styles.section} aria-label={t("popular.ariaLabel")}>
      {greetingText ? <h2 style={styles.greeting}>{greetingText}</h2> : null}
      {chips.length > 0 ? (
        <ul style={styles.list}>
          {chips.map((label, index) => (
            <li key={`${index}-${label}`}>
              <button
                type="button"
                style={{
                  ...styles.item,
                  opacity: disabled ? 0.55 : 1,
                  cursor: disabled ? "not-allowed" : "pointer",
                }}
                disabled={disabled}
                onClick={() => onSelectQuestion(label)}
              >
                <SparklesIcon />
                <span>{label}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
